<script setup lang="ts">
import { mdiChevronRight, mdiClose } from "@mdi/js";
import { useQuery } from "@tanstack/vue-query";
import { ISport } from "~/types/sports/sport";
import api from "~/utils/auth/api";
import sportUrlBuilder from "~/utils/sport/sportUrlBuilder";
import t from "~/utils/translate";
import { storeToRefs } from "pinia";
import { useSettings } from "~/store/settings";
import { useBetslip } from "~/store/betslip";
import AToZInner from "~/components/layout/AToZMenu/AToZInner.vue";
import AToZModal from "~/components/layout/AToZMenu/AToZModal.vue";
import { isMobile } from "~/utils/constants";

interface AZSport extends ISport {
	displayName: string;
	sportLink: string;
	sportIcon: string;
}

const settings = useSettings();
const { azMenu } = storeToRefs(settings);
const container = ref();
const clickout = (event: MouseEvent) => {
	if (event.target && !container.value?.contains(event.target as HTMLElement)) {
		azMenu.value = false;
	}
};

const onAfterEnter = () => {
	document.body.classList.add("scroll-lock");

	if (!isMobile) {
		document.addEventListener("click", clickout);
	}
};

const onAfterLeave = () => {
	document.body.classList.remove("scroll-lock");

	if (!isMobile) {
		document.removeEventListener("click", clickout);
	}
};

interface AZSport extends ISport {
	displayName: string;
	sportLink: string;
	sportIcon: string;
}

const route = useRoute();
const isEsports = computed(() => route.params.sport === "esports" || route.fullPath.includes("esports"));
const getImageLink = (sport: ISport) => {
	let sanitizedName = sport?.sportId.replace("esports-", "");
	if (sanitizedName.includes("---")) sanitizedName = sanitizedName.replace("---", "-");
	return `/medialibraries/content.gmgamingsystems.com/Synapse/icons/${sanitizedName}.png`;
};

const { data: sports, isLoading: isSportsLoading } = useQuery<{ sports: ISport[] }>({
	queryKey: ["az-sports", isEsports],
	queryFn: () =>
		api({
			url: isEsports.value ? "/cron/esports/{brand}/{locale}" : "/cron/sports/{brand}/{locale}",
			options: {
				method: "GET",
			},
		}),
});

const { data: sortedSports, isLoading: isSortedSportsLoading } = useQuery<{ [key: string]: AZSport[] }>({
	queryKey: ["sortedSports", isEsports],
	queryFn: () => {
		return new Promise((resolve) => {
			const sortedGroupedObjects: { [key: string]: AZSport[] } = {};

			if (!isSportsLoading.value && sports.value && sports.value.sports.length > 0) {
				const groupedObjects: { [key: string]: AZSport[] } = {};
				const sportsList = sports.value.sports
					.filter((sport) => sport.sportType === "Sport")
					.map((sport) => {
						const translationKey = sport.sportType === "Sport" ? "sports." + sport.sportId : sport.sportId;
						const sportLink =
							sport.redirectURL ||
							sportUrlBuilder({
								sport: sport.sportId,
								fixtureType: sport.hasHighlights ? "highlights" : sport.hasOutrights ? "outrights" : "live",
							});
						return {
							...sport,
							sportLink: sportLink,
							sportIcon: getImageLink(sport),
							displayName: t(translationKey),
						};
					});

				sportsList.forEach((obj) => {
					const firstLetter = obj.displayName.charAt(0).toUpperCase();
					if (!groupedObjects[firstLetter]) {
						groupedObjects[firstLetter] = [];
					}
					groupedObjects[firstLetter].push(obj);
				});

				Object.keys(groupedObjects)
					.sort()
					.forEach((key) => {
						sortedGroupedObjects[key] = groupedObjects[key];
					});
			}

			resolve(sortedGroupedObjects);
		});
	},
	enabled: computed(() => !!sports.value),
});

defineExpose({
	isSportsLoading,
	isSortedSportsLoading,
});
</script>

<template>
	<Transition name="fade" @after-enter="onAfterEnter" @after-leave="onAfterLeave" appear v-if="!isMobile">
		<div
			ref="container"
			v-if="azMenu"
			class="absolute top-0 left-0 lg:bottom-auto w-full z-50 p-4 overflow-hidden bg-dark-700/40 lg:bg-[transparent] md:top-[40px] lg:p-0 lg:top-[48px] max-h-[calc(100vh-300px)] h-[max-content] flex flex-col"
		>
			<div :class="'bg-light-50 rounded-md  overflow-hidden h-full flex flex-col'">
				<div :class="'text-light-50 p-2 bg-dark-900 flex justify-between font-bold'">
					{{ t("a-to-z-sports") }}
					<UiIcon :path="mdiClose" class="w-6 h-6 cursor-pointer top-2 right-2 fill-light-50" @click="azMenu = false" />
				</div>
				<div class="p-2 bg-light-200 dark:bg-dark-800 drop-shadow-sm overflow-y-auto h-[max-content]">
					<AToZInner :sortedSports="sortedSports" v-if="sortedSports && !isSortedSportsLoading" />
				</div>
			</div>
		</div>
	</Transition>
	<AToZModal :sorted-sports="sortedSports" v-if="sortedSports && !isSortedSportsLoading && isMobile" />
</template>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}
</style>
